import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  List,
  Typography,
  Modal,
  Flex,
  DatePicker,
  TimePicker,
  message,
  Empty,
  Space,
  Select,
  Statistic,
  Tag,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import locale from "antd/es/date-picker/locale/zh_CN";

dayjs.locale("zh-cn");

/**
 * 便民工具推荐：手机端“智能账单记账本”小工具
 *
 * 功能亮点：
 * - 支持添加账单（类型、金额、日期、时间、备注）
 * - 支持编辑、删除账单
 * - 支持月度收支统计、进度条展示
 * - 分类可动态添加，且本地持久化
 * - 手机端友好布局
 * - 数据本地持久化
 * - 适合公众号端便民使用
 */

const LOCAL_KEY = "smart_bill_list";
const LOCAL_CATEGORY_KEY = "smart_bill_category";

const DEFAULT_CATEGORIES = {
  income: [
    { label: "工资", value: "工资" },
    { label: "奖金", value: "奖金" },
    { label: "理财", value: "理财" },
    { label: "其他", value: "其他" },
  ],
  expense: [
    { label: "餐饮", value: "餐饮" },
    { label: "交通", value: "交通" },
    { label: "购物", value: "购物" },
    { label: "娱乐", value: "娱乐" },
    { label: "生活", value: "生活" },
    { label: "其他", value: "其他" },
  ],
};

const BILL_TYPES = [
  { label: "收入", value: "income" },
  { label: "支出", value: "expense" },
];

function BillBook() {
  const [form] = Form.useForm();
  const [bills, setBills] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterMonth, setFilterMonth] = useState(dayjs());
  const [categoryOptions, setCategoryOptions] = useState(
    DEFAULT_CATEGORIES["expense"]
  );
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [addCatModal, setAddCatModal] = useState(false);
  const [catType, setCatType] = useState("expense");
  const [newCat, setNewCat] = useState("");

  // 加载本地数据
  useEffect(() => {
    const local = localStorage.getItem(LOCAL_KEY);
    if (local) {
      setBills(JSON.parse(local));
    }
    const localCat = localStorage.getItem(LOCAL_CATEGORY_KEY);
    if (localCat) {
      setCategories(JSON.parse(localCat));
    }
  }, []);

  // 数据持久化
  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(bills));
  }, [bills]);

  useEffect(() => {
    localStorage.setItem(LOCAL_CATEGORY_KEY, JSON.stringify(categories));
  }, [categories]);

  // 统计本月收支
  const monthBills = bills.filter(
    (item) =>
      dayjs(item.date).format("YYYY-MM") === filterMonth.format("YYYY-MM")
  );
  const income = monthBills
    .filter((item) => item.type === "income")
    .reduce((sum, cur) => sum + Number(cur.amount), 0);
  const expense = monthBills
    .filter((item) => item.type === "expense")
    .reduce((sum, cur) => sum + Number(cur.amount), 0);

  // 新增/编辑账单
  const handleFinish = (values) => {
    const { type, category, amount, date, time, note } = values;
    if (!type || !category || !amount || !date || !time) {
      message.warning("请填写完整信息");
      return;
    }
    const newBill = {
      id: editing ? editing.id : Date.now(),
      type,
      category,
      amount: Number(amount),
      date: date.format("YYYY-MM-DD"),
      time: time.format("HH:mm"),
      note: note || "",
      created: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };
    if (editing) {
      setBills((bills) =>
        bills.map((item) => (item.id === editing.id ? newBill : item))
      );
      message.success("账单已更新");
    } else {
      setBills((bills) => [newBill, ...bills]);
      message.success("账单已添加");
    }
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    setCategoryOptions(categories["expense"]);
  };

  // 删除账单
  const handleDelete = (id) => {
    Modal.confirm({
      title: "确定要删除该账单吗？",
      onOk: () => {
        setBills((bills) => bills.filter((item) => item.id !== id));
        message.success("已删除");
      },
    });
  };

  // 编辑账单
  const handleEdit = (item) => {
    setEditing(item);
    setModalOpen(true);
    form.setFieldsValue({
      type: item.type,
      category: item.category,
      amount: item.amount,
      date: dayjs(item.date),
      time: dayjs(item.time, "HH:mm"),
      note: item.note,
    });
    setCategoryOptions(categories[item.type]);
  };

  // 新增弹窗
  const openAddModal = () => {
    setEditing(null);
    setModalOpen(true);
    form.resetFields();
    setCategoryOptions(categories["expense"]);
  };

  // 类型切换时，更新分类选项
  const handleTypeChange = (value) => {
    setCategoryOptions(categories[value]);
    form.setFieldValue("category", undefined);
  };

  // 动态添加分类
  const handleAddCategory = () => {
    if (!newCat.trim()) {
      message.warning("请输入分类名称");
      return;
    }
    if (
      categories[catType].some(
        (c) => c.value === newCat.trim() || c.label === newCat.trim()
      )
    ) {
      message.warning("该分类已存在");
      return;
    }
    const updated = {
      ...categories,
      [catType]: [
        ...categories[catType],
        { label: newCat.trim(), value: newCat.trim() },
      ],
    };
    setCategories(updated);
    setCategoryOptions(
      catType === form.getFieldValue("type")
        ? updated[catType]
        : categoryOptions
    );
    setNewCat("");
    setAddCatModal(false);
    message.success("分类添加成功");
  };

  // 手机端样式
  const mobileCardStyle = {
    background: "#fff",
    borderRadius: 12,
    marginBottom: 12,
    boxShadow: "0 2px 8px #f0f1f2",
    padding: 16,
    fontSize: 16,
    lineHeight: 1.5,
  };

  // 计算进度条比例
  let incomePercent = 0;
  let expensePercent = 0;
  if (income === 0 && expense === 0) {
    incomePercent = 0;
    expensePercent = 0;
  } else if (income === 0) {
    incomePercent = 0;
    expensePercent = 100;
  } else if (expense === 0) {
    incomePercent = 100;
    expensePercent = 0;
  } else {
    const total = income + expense;
    incomePercent = (income / total) * 100;
    expensePercent = (expense / total) * 100;
  }

  return (
    <Flex
      vertical
      align="center"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          minHeight: "100vh",
          background: "transparent",
        }}
      >
        <div
          style={{
            textAlign: "center",
            margin: "12px 0 20px 0",
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            智能账单记账本
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 14 }}>
            记录每一笔收支，月度统计一目了然
          </Typography.Text>
        </div>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 12 }}
        >
          <Button
            type="primary"
            shape="round"
            onClick={openAddModal}
            style={{ flex: 1, marginRight: 8 }}
          >
            新增账单
          </Button>
          <DatePicker
            picker="month"
            allowClear={false}
            value={filterMonth}
            onChange={(v) => setFilterMonth(v || dayjs())}
            style={{ width: 120 }}
            inputReadOnly
            locale={locale}
            format="YYYY年MM月"
          />
        </Flex>
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            boxShadow: "0 2px 8px #f0f1f2",
          }}
        >
          <Flex justify="space-between" align="center">
            <Statistic
              title="本月收入"
              value={income}
              precision={2}
              valueStyle={{ color: "#3f8600", fontSize: 18 }}
              prefix="￥"
            />
            <Statistic
              title="本月支出"
              value={expense}
              precision={2}
              valueStyle={{ color: "#cf1322", fontSize: 18 }}
              prefix="￥"
            />
          </Flex>
          <div style={{ marginTop: 12 }}>
            {/* 分段进度条，收入为绿色，支出为红色，按比例展示 */}
            <div
              style={{
                width: "100%",
                height: 18,
                background: "#f5f5f5",
                borderRadius: 8,
                overflow: "hidden",
                display: "flex",
              }}
            >
              <div
                style={{
                  width: `${incomePercent}%`,
                  background: "#52c41a",
                  height: "100%",
                  transition: "width 0.4s",
                }}
              />
              <div
                style={{
                  width: `${expensePercent}%`,
                  background: "#cf1322",
                  height: "100%",
                  transition: "width 0.4s",
                }}
              />
            </div>
            <Flex
              justify="space-between"
              align="center"
              style={{ fontSize: 13, color: "#888", marginTop: 4 }}
            >
              <span>
                收入占比：
                {income + expense === 0 ? "--" : `${incomePercent.toFixed(1)}%`}
              </span>
              <span>
                支出占比：
                {income + expense === 0
                  ? "--"
                  : `${expensePercent.toFixed(1)}%`}
              </span>
              <span>
                结余：
                {income + expense === 0
                  ? "--"
                  : `￥${(income - expense).toFixed(2)}`}
              </span>
            </Flex>
          </div>
        </div>
        <div>
          {monthBills.length === 0 ? (
            <Empty description="本月暂无账单" style={{ marginTop: 40 }} />
          ) : (
            <List
              dataSource={monthBills}
              renderItem={(item) => (
                <div style={mobileCardStyle} key={item.id}>
                  <Flex justify="space-between" align="center">
                    <div>
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 17,
                          color: item.type === "income" ? "#3f8600" : "#cf1322",
                        }}
                      >
                        {item.type === "income" ? "+" : "-"}
                        {item.amount}
                      </Typography.Text>
                      <Tag
                        color={item.type === "income" ? "green" : "red"}
                        style={{ marginLeft: 8, fontSize: 14 }}
                      >
                        {item.category}
                      </Tag>
                      <div
                        style={{ color: "#888", fontSize: 13, margin: "4px 0" }}
                      >
                        {dayjs(item.date).format("YYYY年MM月DD日")} {item.time}
                      </div>
                      {item.note && (
                        <Typography.Text
                          style={{ fontSize: 14, color: "#79573e" }}
                        >
                          备注：{item.note}
                        </Typography.Text>
                      )}
                    </div>
                    <Space>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => handleEdit(item)}
                        style={{ padding: 0 }}
                      >
                        编辑
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => handleDelete(item.id)}
                        style={{ padding: 0 }}
                      >
                        删除
                      </Button>
                    </Space>
                  </Flex>
                </div>
              )}
            />
          )}
        </div>
      </div>
      <Modal
        open={modalOpen}
        title={editing ? "编辑账单" : "新增账单"}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
          setCategoryOptions(categories["expense"]);
        }}
        footer={null}
        centered
        bodyStyle={{ padding: 16 }}
        style={{ top: 40 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            type: "expense",
            category: undefined,
            amount: "",
            date: dayjs(),
            time: dayjs(),
            note: "",
          }}
        >
          <Form.Item
            name="type"
            label="类型"
            rules={[{ required: true, message: "请选择类型" }]}
          >
            <Select
              options={BILL_TYPES}
              onChange={handleTypeChange}
              placeholder="请选择类型"
            />
          </Form.Item>
          <Form.Item
            name="category"
            label={
              <Flex align="center" gap={8}>
                <span>分类</span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, fontSize: 13 }}
                  onClick={() => {
                    setCatType(form.getFieldValue("type") || "expense");
                    setAddCatModal(true);
                  }}
                >
                  +添加分类
                </Button>
              </Flex>
            }
            rules={[{ required: true, message: "请选择分类" }]}
          >
            <Select
              options={categoryOptions}
              placeholder="请选择分类"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div
                    style={{ padding: 8, textAlign: "center", color: "#888" }}
                  >
                    如无合适分类，可点击“+添加分类”
                  </div>
                </>
              )}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="金额"
            rules={[
              { required: true, message: "请输入金额" },
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: "金额格式不正确",
              },
            ]}
          >
            <Input placeholder="如：88.88" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="date"
            label="日期"
            rules={[{ required: true, message: "请选择日期" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(d) => d.isAfter(dayjs())}
              inputReadOnly
              locale={locale}
              format="YYYY年MM月DD日"
            />
          </Form.Item>
          <Form.Item
            name="time"
            label="时间"
            rules={[{ required: true, message: "请选择时间" }]}
          >
            <TimePicker
              style={{ width: "100%" }}
              format="HH:mm"
              inputReadOnly
              locale={locale}
            />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea
              placeholder="可填写详细事项"
              maxLength={100}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              shape="round"
              style={{ marginTop: 8 }}
            >
              {editing ? "保存修改" : "添加账单"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={addCatModal}
        title="添加新分类"
        onCancel={() => {
          setAddCatModal(false);
          setNewCat("");
        }}
        onOk={handleAddCategory}
        okText="添加"
        cancelText="取消"
        centered
        bodyStyle={{ padding: 16 }}
      >
        <Form layout="vertical">
          <Form.Item label="分类类型">
            <Select
              value={catType}
              options={BILL_TYPES}
              onChange={(v) => setCatType(v)}
            />
          </Form.Item>
          <Form.Item label="分类名称">
            <Input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              placeholder="请输入分类名称"
              maxLength={10}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
}

export default BillBook;
